import { restApi } from '../../../../services/api';
import { SignatureContextType } from '../contexts/signature.context.types';
import { CompleteSignedContract } from '../signed-contract';

type SignContractValues = {
  orderId: string;
  signedContractId?: string;
  newSignedContract: string;
  signedAt: Date;
  lastSignImageSize: number;
  signingAs: SignatureContextType.SigningAs;
  name: string;
  email: string;
  phone: string;
  lat?: number;
  lng?: number;
  accuracy?: number;
  hashAfterSign: string;
  sign: Buffer;
};

type GenerateSignedContractPdfResponse = {
  url: string;
  signedAt: Date;
  lastSignImageSize: number;
  hash: string;
};

export const signedContractRepository = () => {
  const baseUrl = 'signed-contract';
  const generateContractPdf = (orderId: string) =>
    restApi.get(`${baseUrl}/${orderId}/generate-contract-pdf`);

  const getContractTitle = (orderId: string) => restApi.get(`${baseUrl}/${orderId}/title`);

  const getSignedContracts = (orderId: string) => restApi.get(`${baseUrl}/${orderId}/all`);

  const getExistent = (contractId: string, readOnly?: boolean) =>
    restApi.get(`${baseUrl}/${contractId}/existent?readOnly=${readOnly}`);

  const createEmptySignedContract = (orderId: string) =>
    restApi.post(`${baseUrl}/${orderId}/create-empty-signed-contract`);

  const generateSignedContractPdf = (
    sign: Buffer,
    contractUrl: string,
    signingAs: SignatureContextType.SigningAs,
    signedContractId?: string,
  ) => {
    const formData = new FormData();
    formData.append('sign', new Blob([sign as unknown as BlobPart], { type: 'image/png' }));
    formData.append('contractUrl', contractUrl);
    formData.append('signedContractId', signedContractId || '');
    formData.append('signingAs', signingAs || '');

    return restApi.post<GenerateSignedContractPdfResponse>(
      `${baseUrl}/generate-signed-contract-pdf`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  };

  const getSignExternalLink = (signedContractId: string, signAs: SignatureContextType.SigningAs) =>
    restApi.get<{ id: string }>(
      `${baseUrl}/${signedContractId}/sign-external-link?signAs=${signAs}`,
    );

  const getContractInfoByExternalLink = (externalLinkId: string) =>
    restApi.get<{
      signAs: SignatureContextType.SigningAs;
      signedContract: CompleteSignedContract;
    }>(`${baseUrl}/contract-info-by-external-link/${externalLinkId}`);

  const signContract = ({
    orderId,
    signedContractId,
    newSignedContract,
    signedAt,
    lastSignImageSize,
    signingAs,
    lat,
    lng,
    email,
    name,
    phone,
    hashAfterSign,
    sign,
    accuracy,
  }: SignContractValues) => {
    const formatedSignedAt = typeof signedAt === 'string' ? signedAt : signedAt.toISOString();

    const formData = new FormData();
    formData.append('orderId', orderId);
    formData.append('signedContractId', signedContractId || '');
    formData.append('newSignedContract', newSignedContract);
    formData.append('signedAt', formatedSignedAt);
    formData.append('lastSignImageSize', lastSignImageSize.toString());
    formData.append('signingAs', signingAs || '');
    formData.append('lat', lat?.toString() || '');
    formData.append('lng', lng?.toString() || '');
    formData.append('accuracy', accuracy?.toString() || '');
    formData.append('email', email);
    formData.append('name', name);
    formData.append('phone', phone);
    formData.append('hashAfterSign', hashAfterSign);
    formData.append('sign', new Blob([sign as unknown as BlobPart], { type: 'image/png' }));

    return restApi.post(`${baseUrl}/save`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const signContractUnauthenticated = ({
    orderId,
    signedContractId,
    newSignedContract,
    signedAt,
    lastSignImageSize,
    signingAs,
    lat,
    lng,
    email,
    name,
    phone,
    hashAfterSign,
    sign,
    accuracy,
  }: SignContractValues) => {
    const formatedSignedAt = typeof signedAt === 'string' ? signedAt : signedAt.toISOString();

    const formData = new FormData();
    formData.append('orderId', orderId);
    formData.append('signedContractId', signedContractId || '');
    formData.append('newSignedContract', newSignedContract);
    formData.append('signedAt', formatedSignedAt);
    formData.append('lastSignImageSize', lastSignImageSize.toString());
    formData.append('signingAs', signingAs || '');
    formData.append('lat', lat?.toString() || '');
    formData.append('lng', lng?.toString() || '');
    formData.append('accuracy', accuracy?.toString() || '');
    formData.append('email', email);
    formData.append('name', name);
    formData.append('phone', phone);
    formData.append('hashAfterSign', hashAfterSign);
    formData.append('sign', new Blob([sign as unknown as BlobPart], { type: 'image/png' }));

    return restApi.post(`${baseUrl}/save-unauthenticated`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const generateSignedContractPdfUnauthenticated = (
    sign: Buffer,
    contractUrl: string,
    signingAs: SignatureContextType.SigningAs,
    signedContractId?: string,
  ) => {
    const formData = new FormData();
    formData.append('sign', new Blob([sign as unknown as BlobPart], { type: 'image/png' }));
    formData.append('contractUrl', contractUrl);
    formData.append('signedContractId', signedContractId || '');
    formData.append('signingAs', signingAs || '');

    return restApi.post<GenerateSignedContractPdfResponse>(
      `${baseUrl}/generate-signed-contract-pdf-unauthenticated`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  };

  const sendEmailToSignContract = (contractId: string, orderId: string) =>
    restApi.post(`${baseUrl}/${contractId}/send-signature-email`, { orderId });

  return {
    generateContractPdf,
    getContractTitle,
    getSignedContracts,
    getExistent,
    createEmptySignedContract,
    generateSignedContractPdf,
    getSignExternalLink,
    getContractInfoByExternalLink,
    signContract,
    signContractUnauthenticated,
    generateSignedContractPdfUnauthenticated,
    sendEmailToSignContract,
  };
};
